import { supabase } from '@/lib/supabaseClient'
import type { ExtractedRiskField } from '@/types'

// Nanonets configuration - can be overridden via setConfig
let nanonetsConfig = {
  apiKey: import.meta.env.VITE_NANONETS_API_KEY || '',
  modelId: import.meta.env.VITE_NANONETS_MODEL_ID || '768fe153-a6f2-4b98-b2e7-14444bfa8e1d'
}

// Upload permission - disabled by default, must be enabled by admin
let uploadEnabled = import.meta.env.VITE_UPLOAD_ENABLED === 'true' || false

// Store last raw response for debugging
let lastRawResponse: unknown = null

// Helper to convert label to a field key (snake_case)
function labelToFieldKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
}

class ExtractionService {
  /**
   * Set Nanonets configuration (API key and model ID)
   */
  setConfig(config: { apiKey?: string; modelId?: string }) {
    if (config.apiKey) nanonetsConfig.apiKey = config.apiKey
    if (config.modelId) nanonetsConfig.modelId = config.modelId
  }

  /**
   * Get current Nanonets configuration
   */
  getConfig() {
    return { 
      hasApiKey: !!nanonetsConfig.apiKey,
      modelId: nanonetsConfig.modelId 
    }
  }

  /**
   * Enable or disable uploads
   */
  setUploadEnabled(enabled: boolean) {
    uploadEnabled = enabled
    console.log('Upload enabled:', enabled)
  }

  /**
   * Check if uploads are enabled
   */
  isUploadEnabled() {
    return uploadEnabled
  }

  /**
   * Get last raw Nanonets response for debugging
   */
  getLastRawResponse() {
    return lastRawResponse
  }

  /**
   * Call Nanonets API directly with a file - EXACTLY like ApiTest.tsx
   */
  async callNanonets(file: File | Blob, fileName: string): Promise<{
    success: boolean
    predictions: Array<{ key: string; label: string; value: string; confidence: number }>
    rawResponse: unknown
    error?: string
  }> {
    if (!nanonetsConfig.apiKey) {
      return {
        success: false,
        predictions: [],
        rawResponse: null,
        error: 'Nanonets API key not configured. Go to Settings → API Keys to set it.'
      }
    }

    try {
      const formData = new FormData()
      formData.append('file', file, fileName)

      // Determine which endpoint to use - EXACTLY like ApiTest.tsx
      const endpoint = nanonetsConfig.modelId
        ? `https://app.nanonets.com/api/v2/OCR/Model/${nanonetsConfig.modelId}/LabelFile/`
        : 'https://app.nanonets.com/api/v2/OCR/FullText'

      console.log('Calling Nanonets:', endpoint)

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(nanonetsConfig.apiKey + ':')}`
        },
        body: formData
      })

      const data = await response.json()
      lastRawResponse = data
      console.log('Nanonets raw response:', data)

      if (!response.ok) {
        return {
          success: false,
          predictions: [],
          rawResponse: data,
          error: data.message || data.error || `API returned ${response.status}`
        }
      }

      // Extract predictions - EXACTLY like ApiTest.tsx
      const predictions: Array<{ key: string; label: string; value: string; confidence: number }> = []

      if (data.results && Array.isArray(data.results)) {
        for (const result of data.results) {
          // Method 1: results[].prediction (Model-based OCR)
          if (result.prediction && Array.isArray(result.prediction)) {
            for (const pred of result.prediction) {
              if (pred.ocr_text && pred.label) {
                predictions.push({
                  key: labelToFieldKey(pred.label),
                  label: pred.label,
                  value: pred.ocr_text,
                  confidence: pred.score || 0.5
                })
              }
            }
          }
        }
      }

      console.log(`Extracted ${predictions.length} predictions`)

      return {
        success: true,
        predictions,
        rawResponse: data
      }
    } catch (error) {
      console.error('Nanonets error:', error)
      return {
        success: false,
        predictions: [],
        rawResponse: null,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Process document - downloads from Supabase and calls Nanonets
   */
  async processDocument(documentId: string, submissionId: string): Promise<{
    success: boolean
    fieldsExtracted: number
    rawResponse: unknown
    error?: string
  }> {
    // Update document status to processing
    await supabase
      .from('documents')
      .update({ status: 'processing' })
      .eq('id', documentId)

    try {
      // Get document details
      const { data: document, error: docError } = await supabase
        .from('documents')
        .select('*')
        .eq('id', documentId)
        .single()

      if (docError || !document) {
        throw new Error('Document not found: ' + (docError?.message || 'unknown'))
      }

      console.log('Processing document:', document.file_name, document.storage_path)

      // Download file from storage
      const { data: fileData, error: downloadError } = await supabase.storage
        .from('documents')
        .download(document.storage_path)

      if (downloadError || !fileData) {
        throw new Error('Failed to download document: ' + (downloadError?.message || 'unknown'))
      }

      console.log('Downloaded file, size:', fileData.size)

      // Call Nanonets
      const result = await this.callNanonets(fileData, document.file_name)

      if (!result.success) {
        throw new Error(result.error || 'Nanonets extraction failed')
      }

      // Delete existing fields for this document
      await supabase
        .from('extracted_risk_fields')
        .delete()
        .eq('document_id', documentId)

      // Insert new fields if we have any
      if (result.predictions.length > 0) {
        const fieldsToInsert = result.predictions.map((pred) => ({
          document_id: documentId,
          submission_id: submissionId,
          field_key: pred.key,
          field_label: pred.label,
          extracted_value: pred.value,
          confidence_score: pred.confidence,
          extraction_method: 'ocr' as const,
          is_verified: false
        }))

        const { error: insertError } = await supabase
          .from('extracted_risk_fields')
          .insert(fieldsToInsert)

        if (insertError) {
          console.error('Error inserting fields:', insertError)
          throw new Error(`Failed to save extracted fields: ${insertError.message}`)
        }
      }

      // Update document status to completed
      await supabase
        .from('documents')
        .update({ status: 'completed' })
        .eq('id', documentId)

      // Update submission status to review
      await supabase
        .from('submissions')
        .update({ status: 'review' })
        .eq('id', submissionId)

      return {
        success: true,
        fieldsExtracted: result.predictions.length,
        rawResponse: result.rawResponse
      }

    } catch (error) {
      console.error('Processing error:', error)
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      
      // Update document status to failed
      await supabase
        .from('documents')
        .update({ 
          status: 'failed', 
          error_message: errorMessage
        })
        .eq('id', documentId)

      return {
        success: false,
        fieldsExtracted: 0,
        rawResponse: lastRawResponse,
        error: errorMessage
      }
    }
  }

  /**
   * Get extracted fields for a document
   */
  async getExtractedFields(documentId: string): Promise<ExtractedRiskField[]> {
    const { data, error } = await supabase
      .from('extracted_risk_fields')
      .select('*')
      .eq('document_id', documentId)
      .order('field_key')

    if (error) {
      console.error('Error fetching extracted fields:', error)
      throw error
    }

    return data || []
  }

  /**
   * Get all extracted fields for a submission (across all documents)
   */
  async getSubmissionFields(submissionId: string): Promise<ExtractedRiskField[]> {
    const { data, error } = await supabase
      .from('extracted_risk_fields')
      .select('*')
      .eq('submission_id', submissionId)
      .order('field_key')

    if (error) {
      console.error('Error fetching submission fields:', error)
      throw error
    }

    return data || []
  }

  /**
   * Update a field's corrected value
   */
  async updateFieldValue(
    fieldId: string,
    correctedValue: string | null,
    isVerified: boolean
  ): Promise<ExtractedRiskField> {
    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('extracted_risk_fields')
      .update({
        corrected_value: correctedValue,
        is_verified: isVerified,
        verified_by: isVerified ? user?.id : null,
        verified_at: isVerified ? new Date().toISOString() : null
      })
      .eq('id', fieldId)
      .select()
      .single()

    if (error) {
      console.error('Error updating field:', error)
      throw error
    }

    return data
  }

  /**
   * Subscribe to real-time updates for document status
   */
  subscribeToDocumentStatus(
    documentId: string,
    callback: (document: { status: string; error_message: string | null }) => void
  ) {
    return supabase
      .channel(`document:${documentId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'documents',
          filter: `id=eq.${documentId}`
        },
        (payload) => {
          callback(payload.new as { status: string; error_message: string | null })
        }
      )
      .subscribe()
  }

  /**
   * Subscribe to real-time updates for extracted fields
   */
  subscribeToExtractedFields(
    submissionId: string,
    callback: (field: ExtractedRiskField) => void
  ) {
    return supabase
      .channel(`fields:${submissionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'extracted_risk_fields',
          filter: `submission_id=eq.${submissionId}`
        },
        (payload) => {
          callback(payload.new as ExtractedRiskField)
        }
      )
      .subscribe()
  }
}

export const extractionService = new ExtractionService()
